// src/components/markets/MarketsRealtime.jsx
import React, { useState, useEffect, useRef } from "react";
import { fetchTopAssets, binanceTickersSocket } from "../../services/cryptoWS";
import CryptoChart from "./CryptoChart"; // Import the chart component
import "./cryptoTable.css";

export default function MarketsRealtime() {
  const [view, setView] = useState("crypto");
  const [crypto, setCrypto] = useState([]);
  const [cryptoLoading, setCryptoLoading] = useState(true);
  const [cryptoError, setCryptoError] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null); // State for selected asset

  const cryptoRef = useRef(crypto);
  cryptoRef.current = crypto;

  useEffect(() => {
    let ws;

    async function load() {
      try {
        const assets = await fetchTopAssets(50);
        setCrypto(assets);
        setCryptoLoading(false);
        setCryptoError(null);

        ws = binanceTickersSocket(tickers => {
          const assetMap = new Map(cryptoRef.current.map(c => [c.symbol, c]));
          
          for (const ticker of tickers) {
            const asset = assetMap.get(ticker.symbol);
            if (asset) {
              asset.priceUsd = ticker.priceUsd;
              asset.changePercent24Hr = ticker.changePercent24Hr;
            }
          }
          
          setCrypto([...assetMap.values()]);
        });

      } catch (err) {
        setCryptoError(err.message);
      }
    }

    load();
    
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  const handleRowClick = (symbol) => {
    setSelectedAsset(prev => prev === symbol ? null : symbol); // Toggle selection
  };

  return (
    <div className="markets-page stack gap-6">
      <header className="card markets-header wide-header">
        <div></div>
        <nav className="markets-nav">
          <Tab label="Crypto" id="crypto" view={view} setView={setView} />
        </nav>
      </header>

      {view === "crypto" && (
        <section className="card crypto-card">
          <header className="crypto-head">
            <div>
              <h3 className="crypto-title">Top 50 Crypto Currencies USD Price</h3>
              <p className="muted small">Live prices from Binance. Click a row to view chart.</p>
            </div>
            <span className="badge subtle">{crypto.length}</span>
          </header>

          {cryptoLoading && <p className="muted small">Loading…</p>}
          {cryptoError && <p className="error small">{cryptoError}</p>}

          {!cryptoLoading && !cryptoError && (
            <div className="crypto-table-container">
              <table className="crypto-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Asset</th>
                    <th className="right">Price (USDT)</th>
                    <th className="right">24h %</th>
                    <th className="right">Volume</th>
                  </tr>
                </thead>

                <tbody>
                  {crypto.map((c) => {
                    const up = c.changePercent24Hr >= 0;
                    return (
                      <tr key={c.id} onClick={() => handleRowClick(c.symbol)} className={selectedAsset === c.symbol ? 'row-selected' : ''}>
                        <td className="rank">{c.rank}</td>
                        <td className="symbol">
                          <span className="ticker">{c.symbol}</span>
                        </td>
                        <td className="right price">
                          ${c.priceUsd.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 6,
                          })}
                        </td>
                        <td className="right">
                          <span className={`change ${up ? "up" : "down"}`}>
                            {up ? "▲" : "▼"} {c.changePercent24Hr.toFixed(2)}%
                          </span>
                        </td>
                        <td className="right volume">
                          {c.volumeUsd24Hr
                            ? Number(c.volumeUsd24Hr).toLocaleString()
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {selectedAsset && (
            <div className="chart-container">
              <header className="chart-header">
                <h4>Price History for {selectedAsset}</h4>
                <button className="btn small" onClick={() => setSelectedAsset(null)}>Close</button>
              </header>
              <CryptoChart symbol={selectedAsset} />
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Tab({ label, id, view, setView }) {
  return (
    <button
      className={`seg-btn ${view === id ? "seg-btn-active" : ""}`}
      onClick={() => setView(id)}
    >
      {label}
    </button>
  );
}